import React from 'react';

interface TabItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

interface TabsProps {
  items: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'pills' | 'underline';
}

const sizeClasses = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export const Tabs: React.FC<TabsProps> = ({
  items,
  activeTab,
  onTabChange,
  className = '',
  size = 'md',
  variant = 'default',
}) => {
  const getTabClasses = (item: TabItem, isActive: boolean) => {
    const baseClasses = `
      inline-flex items-center font-medium transition-colors duration-200
      ${sizeClasses[size]}
      ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    `;

    if (variant === 'pills') {
      return `
        ${baseClasses}
        rounded-lg
        ${isActive
          ? 'bg-primary-100 text-primary-700'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        }
        ${item.disabled ? 'hover:bg-transparent hover:text-gray-500' : ''}
      `;
    }

    if (variant === 'underline') {
      return `
        ${baseClasses}
        border-b-2 -mb-px
        ${isActive
          ? 'border-primary-500 text-primary-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }
        ${item.disabled ? 'hover:border-transparent hover:text-gray-500' : ''}
      `;
    }

    // Default variant
    return `
      ${baseClasses}
      border border-gray-300 -ml-px first:ml-0 first:rounded-l-lg last:rounded-r-lg
      ${isActive
        ? 'bg-primary-50 border-primary-500 text-primary-700 z-10'
        : 'bg-white text-gray-500 hover:text-gray-700'
      }
      ${item.disabled ? 'hover:text-gray-500' : ''}
    `;
  };

  const containerClasses = () => {
    if (variant === 'pills') {
      return 'flex space-x-1 bg-gray-100 p-1 rounded-lg';
    }
    if (variant === 'underline') {
      return 'flex border-b border-gray-200';
    }
    return 'flex';
  };

  return (
    <div className={`${className}`}>
      <nav className={containerClasses()}>
        {items.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => !item.disabled && onTabChange(item.id)}
              className={getTabClasses(item, isActive)}
              disabled={item.disabled}
              type="button"
            >
              {Icon && (
                <Icon className={`w-4 h-4 ${item.label ? 'mr-2' : ''}`} />
              )}
              {item.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};